import * as React from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import {
  Edit as EditIcon,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import {
  TextField,
  Box,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  IconButton,
  Chip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { API_BASE_URL, getAuthHeaders } from "../API/Api";

const QUOTE_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "in_review", label: "In Review" },
  { value: "quoted", label: "Quoted" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_FILTERS = [{ value: "all", label: "All" }, ...QUOTE_STATUSES];

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const formatPhone = (countryCode, phone) => {
  const parts = [countryCode, phone].filter(Boolean);
  return parts.join(" ").trim();
};

const getApiErrorMessage = (errorData, fallback) => {
  if (errorData?.message) return errorData.message;
  const errors = errorData?.errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat().find(Boolean);
    if (first) return String(first);
  }
  return fallback;
};

const getQuoteUser = (quote) => quote?.user || {};

const normalizeQuote = (item, index) => {
  const user = getQuoteUser(item);

  return {
    index: index + 1,
    id: item.id,
    reference: item.reference || "",
    name: user.name || item.name || "",
    email: user.email || item.email || "",
    country_code: user.country_code || item.country_code || "",
    phone: user.phone || item.phone || "",
    company: user.company || item.company || "",
    county: user.county || item.county || "",
    customer_note: item.customer_note ?? "",
    status: item.status || "",
    lines_count: Array.isArray(item.lines) ? item.lines.length : 0,
    created_at: item.created_at || "",
    raw: item,
  };
};

const GetQuotes = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  useEffect(() => {
    getApiQuotes();
  }, []);

  const getApiQuotes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/quotes`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      const responseData = await res.json();
      const quotes = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];

      setDataSource(quotes.map(normalizeQuote));
    } catch (error) {
      console.error("Error fetching quotes:", error);
      setDataSource([]);
      message.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (quoteId, newStatus, previousStatus) => {
    if (!quoteId || newStatus === previousStatus) return;

    setUpdatingStatusId(quoteId);
    try {
      const res = await fetch(`${API_BASE_URL}/admin/quotes/${quoteId}`, {
        method: "PATCH",
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });

      const responseData = await res.json().catch(() => ({}));

      if (!res.ok) {
        const validationError = responseData?.errors?.status?.[0];
        throw new Error(
          validationError ||
            getApiErrorMessage(
              responseData,
              `HTTP error! Status: ${res.status}`,
            ),
        );
      }

      setDataSource((prev) =>
        prev.map((item) => {
          if (item.id !== quoteId) return item;
          const updatedRaw = {
            ...(item.raw ?? {}),
            ...(responseData?.data ?? {}),
            status: newStatus,
          };
          return normalizeQuote(updatedRaw, item.index - 1);
        }),
      );
      message.success("Status updated");
    } catch (error) {
      console.error("Error updating quote status:", error);
      message.error(error.message || "Failed to update status");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const columns = [
    { field: "index", headerName: "#", width: 70 },
    { field: "reference", headerName: "Reference", width: 140 },
    { field: "name", headerName: "Name", width: 160 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "company", headerName: "Company", width: 150 },
    {
      field: "phone",
      headerName: "Phone",
      width: 160,
      valueGetter: (params) =>
        formatPhone(params.row.country_code, params.row.phone),
    },
    {
      field: "lines_count",
      headerName: "Items",
      width: 80,
    },
    {
      field: "status",
      headerName: "Status",
      width: 220,
      renderCell: (params) => {
        const isUpdating = updatingStatusId === params.row.id;

        return (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Select
              size="small"
              value={params.row.status || ""}
              disabled={isUpdating}
              onChange={(e) => {
                e.stopPropagation();
                handleStatusUpdate(
                  params.row.id,
                  e.target.value,
                  params.row.status,
                );
              }}
              displayEmpty
              sx={{ minWidth: 130, height: 32, fontSize: "0.8rem" }}
              renderValue={(value) => {
                const option = QUOTE_STATUSES.find(
                  (item) => item.value === value,
                );
                return option?.label || value || "—";
              }}
            >
              {QUOTE_STATUSES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <IconButton
              size="small"
              title="Edit status & pricing"
              onClick={() => navigate(`/admin/quotes/${params.row.id}/status`)}
              sx={{
                color: "#3A6F43",
                border: "1px solid rgba(58, 111, 67, 0.35)",
                borderRadius: 1,
                width: 32,
                height: 32,
                "&:hover": {
                  backgroundColor: "rgba(58, 111, 67, 0.08)",
                },
              }}
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: "created_at",
      headerName: "Submitted",
      width: 170,
      valueGetter: (params) => formatDate(params.row.created_at),
    },
  ];

  const filteredRows = dataSource.filter((row) => {
    if (statusFilter !== "all" && row.status !== statusFilter) {
      return false;
    }

    const term = searchText.toLowerCase();
    if (!term) return true;
    const { raw, ...searchable } = row;
    return Object.values(searchable).some((val) =>
      String(val).toLowerCase().includes(term),
    );
  });

  const getStatusCount = (status) => {
    if (status === "all") return dataSource.length;
    return dataSource.filter((row) => row.status === status).length;
  };

  const start = paginationModel.page * paginationModel.pageSize;
  const end = start + paginationModel.pageSize;
  const pagedRows = filteredRows.slice(start, end);

  const gridHeight = Math.max(220, pagedRows.length * 52 + 56);

  return (
    <div style={{ margin: "25px", overflow: "hidden" }}>
      <Box
        sx={{
          padding: "16px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search Quotes"
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 400,
            backgroundColor: "#f5f5f5",
          }}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        />

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {STATUS_FILTERS.map((status) => {
            const isActive = statusFilter === status.value;
            const count = getStatusCount(status.value);

            return (
              <Chip
                key={status.value}
                label={`${status.label} (${count})`}
                clickable
                onClick={() => {
                  setStatusFilter(status.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                sx={{
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  backgroundColor: isActive ? "#3A6F43" : "#f0f0f0",
                  color: isActive ? "#fff" : "#555",
                  "&:hover": {
                    backgroundColor: isActive ? "#2f5a36" : "#e0e0e0",
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#3f51b5" }} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Quotes...
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            mb: 10,
            p: 2,
            overflowX: "auto",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 1,
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Rows per page:
              </Typography>
              <Select
                value={paginationModel.pageSize}
                onChange={(e) =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    pageSize: e.target.value,
                    page: 0,
                  }))
                }
                size="small"
                sx={{ height: "36px", minWidth: "80px" }}
              >
                {[10, 20, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Typography variant="body2">
              {`${start + 1}-${Math.min(end, filteredRows.length)} of ${
                filteredRows.length
              }`}
            </Typography>

            <Box>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: Math.max(prev.page - 1, 0),
                  }))
                }
                disabled={paginationModel.page === 0}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }))
                }
                disabled={
                  (paginationModel.page + 1) * paginationModel.pageSize >=
                  filteredRows.length
                }
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>

          <DataGrid
            rows={pagedRows}
            columns={columns}
            disableSelectionOnClick
            hideFooter
            disableColumnResize
            sx={{
              height: gridHeight,
              minWidth: 1150,
              border: 0,
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
            }}
          />
        </Paper>
      )}
    </div>
  );
};

export default GetQuotes;
